import { getDateValueInTimeZone } from "Components/Generic/MilestoneDates";
import { format } from "date-fns-tz";
import { MemberAvailability } from "Models/members/member.availability";
import { Member } from "Models/members/member";
import { zonedTimeToUtc, utcToZonedTime, formatInTimeZone } from "date-fns-tz";
import { endOfDay, startOfDay } from "date-fns";

import { AvailabilityStatus } from "./enums";

type UnavailableHoursType = {
  date: string;
  from: string;
  to: string;
};
interface CheckAvailabilityReturn {
  isAvailable: boolean;
  nextAvailable?: Date;
  unavailableHours?: UnavailableHoursType;
}

function returnCorrectDate(date: Date, isEndDate = false): Date {
  const day = date.getDate();
  const month = date.getMonth();
  const fullYear = date.getFullYear();

  const time = isEndDate ? "23:59" : "00:00";
  const fullDate = `${fullYear}-${(month + 1).toString().padStart(2, "0")}-${day
    .toString()
    .padStart(2, "0")} ${time}`;

  return new Date(fullDate);
}

function validateAvailabilities(availabilities: MemberAvailability[]): MemberAvailability[] {
  if (!availabilities || !availabilities.length) {
    return [];
  }

  const validAbsences = availabilities?.filter((availability) => {
    return [AvailabilityStatus.IN_PROGRESS, AvailabilityStatus.PENDING].includes(
      availability.status as AvailabilityStatus
    );
  });

  if (!validAbsences || !validAbsences?.length) {
    return [];
  }

  return validAbsences.sort((a, b) => {
    const dateA = new Date(a.dateFrom!);
    const dateB = new Date(b.dateFrom!);
    return dateB.getTime() - dateA.getTime();
  });
}

export const checkAvailability = (
  checkDate: Date,
  availabilities?: MemberAvailability[],
  timezone?: string
): CheckAvailabilityReturn => {
  const currentTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const utcCheckDate = zonedTimeToUtc(startOfDay(checkDate), currentTimezone);
  const checkDateZonedTime = utcToZonedTime(utcCheckDate, currentTimezone);

  const validAbsences = validateAvailabilities(availabilities || []);

  if (!validAbsences?.length) {
    return { isAvailable: true };
  }

  const availability = validAbsences.find((availability) => {
    const startDate = utcToZonedTime(availability.dateFrom!, currentTimezone);
    const endDate = utcToZonedTime(availability.dateTo!, currentTimezone);

    if (availability.allDayAbsence) {
      const startAllDay = returnCorrectDate(startDate);
      const endAllDay = returnCorrectDate(endDate, true);

      return checkDateZonedTime >= startAllDay && checkDateZonedTime <= endAllDay;
    }

    return startDate >= checkDateZonedTime && endDate <= checkDateZonedTime;
  });

  const absenceOfASpecificHours = validAbsences.find((availability) => {
    if (availability.allDayAbsence) {
      return false;
    }

    const startDateWithTimezone = getDateValueInTimeZone(
      availability.dateFrom ?? "",
      "yyyy-MM-dd",
      currentTimezone
    );
    const endDateWithTimezone = getDateValueInTimeZone(
      availability.dateTo ?? "",
      "yyyy-MM-dd",
      currentTimezone
    );
    const checkDateWithTimezone = getDateValueInTimeZone(
      checkDateZonedTime.toISOString() ?? "",
      "yyyy-MM-dd"
    );

    if (startDateWithTimezone === checkDateWithTimezone) {
      if (endDateWithTimezone === checkDateWithTimezone) {
        if (checkDate > new Date()) {
          return true;
        }

        const endDateTime = getDateValueInTimeZone(
          availability.dateTo ?? "",
          "HH:mm",
          currentTimezone
        );
        const checkDateTime = getDateValueInTimeZone(
          checkDate.toISOString() ?? "",
          "HH:mm",
          currentTimezone
        );

        if (!endDateTime || !checkDateTime) return false;

        return endDateTime >= checkDateTime;
      }

      return false;
    }

    return false;
  });

  let unavailableHours;

  if (absenceOfASpecificHours) {
    unavailableHours = {
      date: format(checkDateZonedTime, "do"),
      from:
        getDateValueInTimeZone(absenceOfASpecificHours.dateFrom ?? "", "HH:mm", currentTimezone) ||
        "",
      to:
        getDateValueInTimeZone(absenceOfASpecificHours.dateTo ?? "", "HH:mm", currentTimezone) ||
        "",
    };
  }

  if (!availability) {
    return { isAvailable: true, unavailableHours };
  }

  if (!availability.allDayAbsence && !!unavailableHours) {
    return {
      isAvailable: false,
      nextAvailable: utcToZonedTime(availability.dateTo!, currentTimezone),
      unavailableHours,
    };
  }

  if (availability.allDayAbsence) {
    const nextDay = returnCorrectDate(utcToZonedTime(availability.dateTo!, currentTimezone), true);

    return { isAvailable: false, nextAvailable: zonedTimeToUtc(nextDay, currentTimezone) };
  }

  return { isAvailable: true };
};

export function checkMemberAvailability(
  checkDate: Date,
  member: Member,
  // availabilities?: MemberAvailability[],
  timezone?: string
): CheckAvailabilityReturn {
  if (!member) return { isAvailable: false };

  const { availabilities } = member;

  const validAbsences = validateAvailabilities(availabilities || []);

  if (!validAbsences.length) return { isAvailable: true };

  const currentTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const utcCheckDate = zonedTimeToUtc(
    startOfDay(checkDate),
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const checkDateZonedTime = utcToZonedTime(utcCheckDate, currentTimezone);

  for (const availability of validAbsences) {
    const startDate = utcToZonedTime(availability.dateFrom!, currentTimezone);
    const endDate = utcToZonedTime(availability.dateTo!, currentTimezone);

    if (availability.allDayAbsence) {
      const startAllDay = returnCorrectDate(startDate);
      const endAllDay = returnCorrectDate(endDate, true);

      const isUnavailable = checkDateZonedTime >= startAllDay && checkDateZonedTime <= endAllDay;

      if (isUnavailable) {
        return {
          isAvailable: false,
          nextAvailable: zonedTimeToUtc(endAllDay, currentTimezone),
        };
      }
    } else {
      const startDateWithTimezone = getDateValueInTimeZone(
        availability.dateFrom ?? "",
        "yyyy-MM-dd",
        currentTimezone
      );
      const checkDateWithTimezone = getDateValueInTimeZone(
        checkDateZonedTime.toISOString() ?? "",
        "yyyy-MM-dd"
      );

      // If it's the same date, check time overlap
      if (startDateWithTimezone === checkDateWithTimezone) {
        const unavailableHours = {
          date: format(checkDateZonedTime, "do"),
          from: getDateValueInTimeZone(availability.dateFrom ?? "", "HH:mm", currentTimezone) || "",
          to: getDateValueInTimeZone(availability.dateTo ?? "", "HH:mm", currentTimezone) || "",
        };

        // For future dates or if there's a time conflict, mark as unavailable
        if (
          checkDate > new Date() ||
          (startDate <= checkDateZonedTime && endDate >= checkDateZonedTime)
        ) {
          return {
            isAvailable: false,
            nextAvailable: utcToZonedTime(availability.dateTo!, currentTimezone),
            unavailableHours,
          };
        }
      }
    }
  }

  return { isAvailable: true };
}

export function checkResourceAvailability(
  checkDate: Date,
  availabilities?: MemberAvailability[],
  timezone?: string
): CheckAvailabilityReturn {
  return checkMemberAvailability(
    checkDate,
    { availabilities: availabilities || [] } as Member,
    timezone
  );
}
